// Staff time off, shared by the staff portal (own records, "Take Time Off") and
// the provider portal (approval queue, all records). Mirrors the backend DTOs.

import type { BusinessHoursDay } from './businessHours';

/** Mirrors the backend TimeOffType enum; `label` comes from the server. */
export type TimeOffType =
	| 'VACATION'
	| 'PERSONAL'
	| 'SICK'
	| 'FAMILY_EMERGENCY'
	| 'TRAINING'
	| 'UNAVAILABLE'
	| 'OTHER';

/** The reasons a staff member can pick, in the order the form offers them. */
export const STAFF_TIME_OFF_REASONS: { value: TimeOffType; label: string }[] = [
	{ value: 'VACATION', label: 'Vacation' },
	{ value: 'PERSONAL', label: 'Personal Time Off' },
	{ value: 'SICK', label: 'Sick Leave' },
	{ value: 'FAMILY_EMERGENCY', label: 'Family Emergency' },
	{ value: 'TRAINING', label: 'Training / Certification' },
	{ value: 'OTHER', label: 'Other' },
];

/** The provider's form also offers a bare "Unavailable" block. */
export const PROVIDER_TIME_OFF_REASONS: { value: TimeOffType; label: string }[] = [
	...STAFF_TIME_OFF_REASONS.slice(0, 5),
	{ value: 'UNAVAILABLE', label: 'Unavailable' },
	{ value: 'OTHER', label: 'Other' },
];

/**
 * Only BLOCKED and APPROVED remove the staff member's labor. A PENDING request
 * leaves the calendar open; DECLINED, WITHDRAWN and REMOVED never touch it.
 */
export type TimeOffStatus =
	| 'BLOCKED'
	| 'PENDING'
	| 'APPROVED'
	| 'DECLINED'
	| 'WITHDRAWN'
	| 'REMOVED';

export type TimeOffAction =
	| 'BOOKING_RESOLVED'
	| 'CREATED'
	| 'BLOCKED'
	| 'SUBMITTED'
	| 'APPROVED'
	| 'DECLINED'
	| 'WITHDRAWN'
	| 'REMOVED'
	| 'UPDATED';

export interface TimeOffHistoryEntry {
	action: TimeOffAction;
	actorUserId: number | null;
	actorName: string | null;
	actorRole: 'STAFF' | 'PROVIDER' | string;
	at: string;
	note: string | null;
}

/** How one requested day measured against the notice rule. */
export interface TimeOffNoticeDay {
	date: string;
	dayName: string;
	weekend: boolean;
	salonClosed: boolean;
	closedReason: string | null;
	requiredNoticeHours: number;
	providedNoticeHours: number;
	shortNotice: boolean;
}

export interface TimeOffAffectedBooking {
	appointmentId: number;
	customerName: string | null;
	appointmentDateTime: string;
	whenLabel: string;
	serviceName: string | null;
	amount: number | null;
	locationId: number | null;
	locationName: string | null;
	status: string | null;
}

// ─── Story 9: resolving the bookings inside a request ───────────

export type TimeOffBookingResolution =
	| 'UNRESOLVED'
	| 'RESCHEDULED'
	| 'REASSIGNED'
	| 'CANCELLED'
	| 'REFUNDED'
	| 'COMPLETED';

export type TimeOffResolveAction = 'RESCHEDULE' | 'REASSIGN' | 'CANCEL' | 'REFUND';

export interface TimeOffReassignCandidate {
	staffId: number;
	name: string;
	role: string | null;
}

/** One booking inside a request, read fresh off the appointment. */
export interface TimeOffBookingResolutionRow {
	appointmentId: number;
	customerName: string;
	appointmentDateTime: string | null;
	whenLabel: string | null;
	serviceName: string | null;
	amount: number | null;
	locationId: number | null;
	locationName: string | null;
	status: string | null;
	assignedStaffNames: string[];
	resolution: TimeOffBookingResolution;
	resolutionLabel: string;
	resolved: boolean;
	paidAmount: number | null;
	refundedAmount: number | null;
	refundable: boolean;
	notRefundableReason: string | null;
	/** Set when a cancellation went through but the refund did not. */
	refundError: string | null;
	candidates: TimeOffReassignCandidate[];
}

export interface TimeOffResolution {
	timeOffId: number;
	status: TimeOffStatus;
	bookings: TimeOffBookingResolutionRow[];
	unresolvedCount: number;
	unresolvedRevenue: number;
	allResolved: boolean;
	canApprove: boolean;
}

/** POST /time-off/{id}/bookings/{appointmentId}/resolve */
export interface TimeOffBookingResolveRequest {
	action: TimeOffResolveAction;
	appointmentDateTime?: string;
	staffId?: number;
	refund?: boolean;
	reason?: string;
}

/** What was done to one booking, as kept on the request. */
export interface TimeOffBookingResolutionEntry {
	appointmentId: number;
	customerName: string | null;
	resolution: TimeOffBookingResolution;
	detail: string | null;
	actorUserId: number | null;
	actorName: string | null;
	at: string;
}

export interface TimeOffRecord {
	id: number;
	staffId: number;
	staffName: string;
	staffRole: string | null;
	locationId: number | null;
	locationName: string;
	startDate: string;
	endDate: string;
	fullDay: boolean;
	startTime: string | null;
	endTime: string | null;
	type: TimeOffType;
	typeLabel: string;
	notes: string | null;
	dateLabel: string;
	timeLabel: string;
	dayCount: number;
	past: boolean;
	status: TimeOffStatus;
	/** "Blocked", "Waiting on Provider", "Approved", "Declined", or "Completed" once past. */
	statusLabel: string;
	active: boolean;
	requestedByStaff: boolean;
	staffNote: string | null;
	escalationReasons: string[];
	noticeDays: TimeOffNoticeDay[];
	affectedBookingCount: number | null;
	affectedRevenue: number | null;
	/** Present on pending records in the provider list and on single reads. */
	affectedBookings?: TimeOffAffectedBooking[] | null;
	submittedAt: string | null;
	decidedByName: string | null;
	decidedAt: string | null;
	decisionNote: string | null;
	canWithdraw: boolean;
	canRemove: boolean;
	/** Pending requests only: live bookings still assigned to the staff member in the dates. */
	unresolvedBookingCount: number | null;
	/** True when the request is pending and nothing stands in the way of approval. */
	canApprove: boolean;
	/** What the provider did to each affected booking from the review. */
	bookingResolutions: TimeOffBookingResolutionEntry[];
	history: TimeOffHistoryEntry[];
	createdAt: string | null;
	updatedAt: string | null;
}

/** POST /time-off/me and /time-off/me/preview. */
export interface StaffTimeOffRequest {
	startDate: string;
	endDate: string;
	type: TimeOffType;
	note?: string;
	locationId?: number;
}

export type TimeOffPreviewOutcome = 'BLOCK' | 'REQUEST' | 'NOTHING_TO_BLOCK' | 'INVALID';

export interface TimeOffPreview {
	outcome: TimeOffPreviewOutcome;
	canSubmit: boolean;
	blockingMessage: string | null;
	days: TimeOffNoticeDay[];
	openDayCount: number;
	closedDayCount: number;
	noticeShortfalls: TimeOffNoticeDay[];
	affectedBookings: TimeOffAffectedBooking[];
	affectedBookingCount: number;
	affectedRevenue: number;
	requiresApproval: boolean;
	reasons: string[];
	submitLabel: string;
	weekdayNoticeHours: number;
	weekendNoticeHours: number;
}

/** POST /time-off (provider). Full-day or a time window on each day. */
export interface ProviderTimeOffRequest {
	staffId: number;
	locationId?: number | null;
	startDate: string;
	endDate?: string;
	fullDay: boolean;
	startTime?: string;
	endTime?: string;
	type: TimeOffType;
	notes?: string;
}

/** An existing assignment the provider's save landed on. Reported, never cancelled. */
export interface TimeOffSaveAffectedBooking {
	appointmentId: number;
	appointmentDateTime: string;
	whenLabel: string | null;
	serviceName: string | null;
	durationMinutes: number;
	locationId: number | null;
	locationName: string | null;
	staffId: number | null;
	staffName: string | null;
	status: string | null;
}

export interface TimeOffSaveResponse {
	timeOff: TimeOffRecord;
	warning: string | null;
	affectedBookings: TimeOffSaveAffectedBooking[] | null;
}

export interface TimeOffListParams {
	staffId?: number;
	locationId?: number;
	upcomingOnly?: boolean;
	status?: TimeOffStatus | 'ALL';
}

// ─── Salon hours & closures as the staff portal sees them ──────────

export interface LocationClosure {
	id: number;
	locationId: number;
	name: string;
	startDate: string;
	endDate: string;
	dateLabel: string;
	dayCount: number;
	past: boolean;
	createdAt: string | null;
}

export interface LocationClosureRequest {
	name: string;
	startDate: string;
	endDate?: string;
}

export interface LocationClosureSaveResponse {
	closure: LocationClosure;
	warning: string | null;
	affectedBookings: TimeOffSaveAffectedBooking[] | null;
}

export interface StaffSalonLocationHours {
	locationId: number;
	locationName: string;
	timezone: string;
	hours: BusinessHoursDay[];
	closures: LocationClosure[];
}

export interface StaffSalonHours {
	locations: StaffSalonLocationHours[];
	weekdayNoticeHours: number;
	weekendNoticeHours: number;
}
